import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const AdminFormLinksPage = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', url: '', description: '', is_active: true, order_index: 0 });
  const [saving, setSaving] = useState(false);

  const fetchLinks = async () => {
    try {
      const { data } = await api.get('/admin/form-links');
      setLinks(data);
    } catch (err) {
      toast.error('Gagal memuat data link');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLinks(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', url: '', description: '', is_active: true, order_index: links.length });
    setShowModal(true);
  };

  const openEdit = (link) => {
    setEditing(link);
    setForm({ title: link.title, url: link.url, description: link.description || '', is_active: link.is_active, order_index: link.order_index });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.url.trim()) {
      toast.error('Judul dan URL wajib diisi');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/form-links/${editing.id}`, form);
        toast.success('Link berhasil diperbarui');
      } else {
        await api.post('/admin/form-links', form);
        toast.success('Link berhasil ditambahkan');
      }
      setShowModal(false);
      fetchLinks();
    } catch (err) {
      const msg = err.response?.data?.details || err.response?.data?.error || 'Gagal menyimpan';
      toast.error(msg);
      console.error('Form link save error:', err.response?.data);
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (link) => {
    try {
      await api.put(`/admin/form-links/${link.id}`, { is_active: !link.is_active });
      toast.success(`Link ${link.is_active ? 'dinonaktifkan' : 'diaktifkan'}`);
      fetchLinks();
    } catch (err) {
      toast.error('Gagal mengubah status');
    }
  };

  const handleDelete = async (link) => {
    if (!window.confirm(`Hapus link "${link.title}"?`)) return;
    try {
      await api.delete(`/admin/form-links/${link.id}`);
      toast.success('Link berhasil dihapus');
      fetchLinks();
    } catch (err) {
      toast.error('Gagal menghapus link');
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner" /><span>Memuat...</span></div>;
  }

  return (
    <div className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 700 }}>Pengaturan Link Google Form</h2>
        <button className="btn btn-primary" onClick={openCreate}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
          Tambah Link
        </button>
      </div>

      {links.length === 0 ? (
        <div className="empty-state">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" width="48" height="48"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m1.415-1.415a4.5 4.5 0 016.364 6.364l-4.5 4.5" /></svg>
          <p>Belum ada link form. Klik "Tambah Link" untuk menambahkan Google Form.</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="table">
            <thead>
              <tr>
                <th>No</th>
                <th>Judul</th>
                <th>URL</th>
                <th>Deskripsi</th>
                <th>Urutan</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {links.map((link, i) => (
                <tr key={link.id}>
                  <td>{i + 1}</td>
                  <td style={{ fontWeight: 600 }}>{link.title}</td>
                  <td style={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-primary)' }}>{link.url}</a>
                  </td>
                  <td style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {link.description || '-'}
                  </td>
                  <td>{link.order_index}</td>
                  <td>
                    <button
                      className={`btn btn-sm ${link.is_active ? 'btn-success' : 'btn-ghost'}`}
                      onClick={() => handleToggle(link)}
                      style={{ fontSize: '0.75rem' }}
                    >
                      {link.is_active ? 'Aktif' : 'Nonaktif'}
                    </button>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(link)}>
                        <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
                      </button>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => handleDelete(link)}>
                        <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" /></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">{editing ? 'Edit Link' : 'Tambah Link Baru'}</h2>
            <form onSubmit={handleSave}>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Judul *</label>
                <input className="form-input" placeholder="Nama form (misal: Survey Kepuasan)" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required />
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">URL Google Form *</label>
                <input className="form-input" placeholder="https://forms.gle/..." value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))} required />
              </div>
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">Deskripsi</label>
                <textarea className="form-textarea" placeholder="Deskripsi singkat tentang form ini" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} rows={3} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
                <div className="form-group">
                  <label className="form-label">Urutan</label>
                  <input type="number" className="form-input" value={form.order_index} onChange={e => setForm(p => ({ ...p, order_index: parseInt(e.target.value) || 0 }))} min="0" />
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select className="form-select" value={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.value === 'true' }))}>
                    <option value="true">Aktif</option>
                    <option value="false">Nonaktif</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Batal</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <div className="spinner" style={{ width: 18, height: 18, borderWidth: 2 }} /> : (editing ? 'Simpan' : 'Tambah')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFormLinksPage;
